import type { Map as MapLibreMap, SymbolLayerSpecification } from 'maplibre-gl'

/**
 * Where overlays are inserted in the style. Radar over place names is
 * unreadable, so overlays go under the first symbol layer.
 *
 * OpenFreeMap's Positron, Dark and Liberty styles all have symbol layers. The
 * CARTO Voyager fallback is one raster layer with none, and `undefined` there
 * means "append on top" — the only possible answer when the labels are baked
 * into the tiles.
 */
export function firstSymbolLayerId(map: MapLibreMap): string | undefined {
  try {
    return map.getStyle()?.layers?.find((l) => l.type === 'symbol')?.id
  } catch {
    // getStyle() throws if the style is not loaded yet; the caller re-runs on
    // style.load anyway.
    return undefined
  }
}

/**
 * The fontstack the basemap already draws labels with, or null when it has
 * none.
 *
 * Hardcoding "Noto Sans Regular" would be right for all three OpenFreeMap
 * styles and wrong for the CARTO Voyager fallback, whose style is one raster
 * layer with no `glyphs` at all — a symbol layer there is a MapLibre error and
 * an invisible label, so the caller draws no label instead.
 */
export function labelFont(map: MapLibreMap): string[] | null {
  try {
    const style = map.getStyle()
    if (!style?.glyphs) return null
    const symbol = style.layers?.find((l) => l.type === 'symbol')
    const font = (symbol as SymbolLayerSpecification | undefined)?.layout?.['text-font']
    return Array.isArray(font) ? (font as string[]) : null
  } catch {
    // Same as above: getStyle() throws before the style is loaded.
    return null
  }
}
